/**
 * matchText — the ONE text matcher for thread names, routine names and file
 * names (2026-09-09, the search that suggests). Folds accents and case; every
 * query word must match, in ANY order, by exact > prefix > word start >
 * anywhere; a query word of ≥ 4 letters that misses is rescued by a bounded
 * typo distance (1 edit up to 6 letters, else 2 — "invocies" finds
 * "Invoices"; a 3-letter word never rescues, so "inv" cannot drift onto
 * "ink"). The result carries the tier's score and the spans to highlight, in
 * the ORIGINAL text's offsets (accents and case preserved). Pure; shared by
 * the instant corpus and the search UI.
 */
import { editDistanceWithin } from './editDistance';

export interface MatchRange {
  start: number;
  end: number;
}

export type MatchTier = 'exact' | 'prefix' | 'word' | 'substring' | 'fuzzy';

export interface TextMatch {
  score: number;
  tier: MatchTier;
  ranges: MatchRange[];
}

export const MATCH_TIER_SCORE: Readonly<Record<MatchTier, number>> = {
  exact: 100,
  prefix: 80,
  word: 60,
  substring: 40,
  fuzzy: 25,
};

/** A query word shorter than this is never typo-rescued (too many false friends). */
export const FUZZY_MIN_TOKEN = 4;

export interface FoldedText {
  folded: string;
  /** start[i] / end[i]: the original offsets of folded[i]. */
  start: number[];
  end: number[];
}

function foldChar(ch: string): string {
  return ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Fold like foldSearchText (accents, case, single spaces) keeping a map back to the original offsets. */
export function foldWithMap(text: unknown): FoldedText {
  const folded: string[] = [];
  const start: number[] = [];
  const end: number[] = [];
  let at = 0;
  let pendingSpace = false;
  for (const ch of String(text ?? '')) {
    const from = at;
    at += ch.length;
    if (/\s/.test(ch)) {
      if (folded.length > 0) pendingSpace = true;
      continue;
    }
    const f = foldChar(ch);
    if (!f) continue;
    if (pendingSpace) {
      folded.push(' ');
      start.push(from);
      end.push(from);
      pendingSpace = false;
    }
    for (const c of f) {
      folded.push(c);
      start.push(from);
      end.push(at);
    }
  }
  return { folded: folded.join(''), start, end };
}

/** The query's folded words (empty for a blank query). */
export function queryWords(query: unknown): string[] {
  return foldWithMap(query).folded.split(' ').filter(Boolean);
}

interface FoldedHit {
  tier: MatchTier;
  start: number;
  end: number;
}

function findWord(folded: string, token: string): FoldedHit | null {
  if (!token) return null;
  if (folded === token) return { tier: 'exact', start: 0, end: token.length };
  if (folded.startsWith(token)) return { tier: 'prefix', start: 0, end: token.length };
  const idx = folded.indexOf(token);
  if (idx !== -1) {
    return { tier: folded[idx - 1] === ' ' ? 'word' : 'substring', start: idx, end: idx + token.length };
  }
  if (token.length >= FUZZY_MIN_TOKEN && !token.includes(' ')) {
    const max = token.length <= 6 ? 1 : 2;
    let pos = 0;
    for (const word of folded.split(' ')) {
      if (word) {
        const head = word.length > token.length ? word.slice(0, token.length) : word;
        if (editDistanceWithin(token, word, max) || (head !== word && editDistanceWithin(token, head, max))) {
          return { tier: 'fuzzy', start: pos, end: pos + word.length };
        }
      }
      pos += word.length + 1;
    }
  }
  return null;
}

/** Sort and merge overlapping or touching ranges. */
export function mergeRanges(ranges: readonly MatchRange[]): MatchRange[] {
  const sorted = ranges
    .filter((r) => r && Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start)
    .slice()
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const out: MatchRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start <= last.end) {
      if (r.end > last.end) last.end = r.end;
    } else {
      out.push({ start: r.start, end: r.end });
    }
  }
  return out;
}

/**
 * Match a query against a short text (a name). Null when any query word is
 * missing. The whole phrase in order wins its own tier; otherwise every word
 * matches in any order and the weakest word's tier is the match's tier
 * (capped at "word start" — an any-order match never reads as exact).
 */
export function matchText(query: unknown, text: unknown): TextMatch | null {
  const q = foldWithMap(query).folded;
  if (!q) return null;
  const ft = foldWithMap(text);
  if (!ft.folded) return null;
  const toRange = (s: number, e: number): MatchRange => ({ start: ft.start[s], end: ft.end[e - 1] });
  const phrase = findWord(ft.folded, q);
  if (phrase && phrase.tier !== 'fuzzy') {
    return { score: MATCH_TIER_SCORE[phrase.tier], tier: phrase.tier, ranges: [toRange(phrase.start, phrase.end)] };
  }
  const words = q.split(' ').filter(Boolean);
  if (words.length === 1) {
    if (!phrase) return null;
    return { score: MATCH_TIER_SCORE.fuzzy, tier: 'fuzzy', ranges: [toRange(phrase.start, phrase.end)] };
  }
  let weakest: MatchTier = 'word';
  const ranges: MatchRange[] = [];
  for (const w of words) {
    const hit = findWord(ft.folded, w);
    if (!hit) return null;
    if (MATCH_TIER_SCORE[hit.tier] < MATCH_TIER_SCORE[weakest]) weakest = hit.tier;
    ranges.push(toRange(hit.start, hit.end));
  }
  return { score: MATCH_TIER_SCORE[weakest], tier: weakest, ranges: mergeRanges(ranges) };
}

/**
 * Every occurrence of the given (already folded) words inside a text, as
 * original-offset ranges — the highlighter for message snippets, whose
 * scoring lives in the shared tokenizer rather than here.
 */
export function rangesIn(text: unknown, words: readonly string[], maxPerWord: number = 8): MatchRange[] {
  const ft = foldWithMap(text);
  if (!ft.folded) return [];
  const out: MatchRange[] = [];
  for (const raw of words) {
    const w = foldWithMap(raw).folded;
    if (!w) continue;
    let from = 0;
    let n = 0;
    while (n < maxPerWord) {
      const idx = ft.folded.indexOf(w, from);
      if (idx === -1) break;
      out.push({ start: ft.start[idx], end: ft.end[idx + w.length - 1] });
      from = idx + w.length;
      n += 1;
    }
  }
  return mergeRanges(out);
}
